import { Injectable } from '@angular/core';

import { LatLngBounds } from 'leaflet';

//TODO GB wymaga -"allowSyntheticDefaultImports": true
import Supercluster from 'supercluster'
import * as GeoJSON from 'geojson';

import { Feature, Point as Location } from 'geojson';
import { Point } from '../../../models/map/point.model';
import { Cluster } from '../../../models/map/Cluster';

/**
 * Provides clustering utilities
 */
@Injectable()
export class ClusterService {

    /**
     * Clustering index - once loaded, index is immutable
     */
    private index: Supercluster;


    /**
     * Clustering index configuration
     */
    private options: any = {
        minZoom: 2,    // min zoom to generate clusters on
        maxZoom: 18,   // max zoom level to cluster the points on
        radius: 120,    // cluster radius in pixels
        extent: 512,   // tile extent (radius is calculated relative to it)
        nodeSize: 64,  // size of the KD-tree leaf node, affects performance
        log: false     // whether to log timing info
    };


    /**
     * Creates clustering index
     * @param $http http service
     */
    constructor() {

        // Create index
        this.index = new Supercluster(this.options);
    }


    /**
     * Loads points to clustering index - once loaded, index is immutable
     * @param points array of model points
     */
    public load(points: Point[]): void {

        // Map points to features and load them to clustering index
        const features: Supercluster.PointFeature<Location>[] = this.mapModelPointsToGeoJsonFeatures(points);
        this.index.load(features);
    }


    /**
     * Computes and returns clusters
     * @param bounds bounding box array [westLng, southLat, eastLng, northLat]
     * @param zoom level of zoom
     */
    public getClusters(bounds: LatLngBounds, zoom: number): Cluster[] {
        let clusters: Cluster[];
        try {
            // Get geojson clusters and then map them to model groups
            const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] as GeoJSON.BBox;
            const _c = this.index.getClusters(bbox, zoom);
            clusters = this.mapGeoJsonClustersToModelClusters(_c);
        } catch (e) {
            console.error('GETING CLUSTER ERROR', e);
            clusters = [];
        }
        return clusters;
    }


    /**
     * For a given zoom and x/y coordinates, returns a geojson-vt-compatible JSON tile object with cluster/point features
     * @param zoom level of zoom
     * @param x x coordinate
     * @param y y coordinate
     */
    public getTile(zoom: number, x: number, y: number): any {

        this.index.getTile(zoom, x, y);
    }


    /**
     * Returns the children of a cluster (on the next zoom level) given its id (cluster_id value from feature properties)
     * @param clusterId id of cluster
     */
    public getChildren(clusterId: number): Point[] {

        const features: Feature<Location>[] = this.index.getChildren(clusterId);
        return this.mapGeoJsonFeaturesToModelPoints(features);
    }


    /**
     * Returns all the points of a cluster (given its cluster_id), with pagination support:
     * limit is the number of points to return (set to Infinity for all points), and offset is the amount of points to skip (for pagination)
     * @param clusterId id of cluster
     * @param limit number of points to return
     * @param offset amount of points to skip (for pagination).
     */
    public getLeaves(clusterId: number, limit: number = 10, offset: number = 0): Point[] {

        const features: Feature<Location>[] = this.index.getLeaves(clusterId, limit, offset);
        return this.mapGeoJsonFeaturesToModelPoints(features);
    }


    /**
     * Returns the zoom on which the cluster expands into several children
     * (useful for "click to zoom" feature) given the cluster's cluster_id
     * @param clusterId id of cluster
     */
    public getClusterExpansionZoom(clusterId: number): number {

        return this.index.getClusterExpansionZoom(clusterId);
    }


    /**
     * Maps model points to geojson features, affects loading performance
     * @param points array of model points
     */
    private mapModelPointsToGeoJsonFeatures(points: Point[]): Supercluster.PointFeature<Location>[] {

        const features: Supercluster.PointFeature<Location>[] = [];

        for (const point of points) {
            const feature: Supercluster.PointFeature<Location> = {
                type: 'Feature',
                properties: point.properties,
                geometry: {
                    type: 'Point',
                    coordinates: [
                        point.lng,
                        point.lat
                    ]
                },
            };
            features.push(feature);
        }

        return features;
    }


    /**
     * Maps geojson features to model points
     * @param features array of geojson features
     */
    private mapGeoJsonFeaturesToModelPoints(features: Feature<Location>[]): Point[] {

        const points: Point[] = [];

        for (const feature of features) {
            const point: Point = {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0],
                properties: feature.properties
            };
            points.push(point);
        }

        return points.sort((x, y) => x.properties.Id - y.properties.Id);
    }


    /**
     * Maps geojson clusters to model clusters, affects clustering performance
     * @param features array of geojson clusters
     */
    private mapGeoJsonClustersToModelClusters(features: Feature<Location>[]): Cluster[] {

        const clusters: Cluster[] = [];

        for (const f of features) {
            const c = new Cluster();

            c.clusterId = f.properties?.['cluster_id'];
            c.lat = f.geometry.coordinates[1];
            c.lng = f.geometry.coordinates[0];
            c.points = c.clusterId ? this.getLeaves(c.clusterId, Infinity) : [new Point(c.lat, c.lng)];
            c.properties = f.properties;

            clusters.push(c);
        }

        return clusters;
    }
}
